import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Ban,
  Search,
  Upload,
  X,
  ChevronsUpDown,
  Check,
  MapPin,
  Users,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { MarketingContact, Region, Client } from "@shared/schema";

type ContactType = "media" | "audience";

type ContactWithRelations = MarketingContact & {
  regionIds: string[];
  clientIds: string[];
};

const contactFormSchema = z.object({
  type: z.enum(["media", "audience"]),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  notes: z.string().optional(),
  outlet: z.string().optional(),
  beat: z.string().optional(),
  mediaType: z.string().optional(),
  optInEmail: z.boolean().optional(),
  optInSms: z.boolean().optional(),
  source: z.string().optional(),
  regionIds: z.array(z.string()).optional(),
  clientIds: z.array(z.string()).optional(),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

const emptyForm = (type: ContactType): ContactFormValues => ({
  type,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  notes: "",
  outlet: "",
  beat: "",
  mediaType: "",
  optInEmail: false,
  optInSms: false,
  source: "",
  regionIds: [],
  clientIds: [],
});

export default function MarketingContactsPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<ContactType>("media");
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [regionFilterOpen, setRegionFilterOpen] = useState(false);
  const [editing, setEditing] = useState<ContactWithRelations | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<ContactWithRelations | null>(null);

  const { data: contacts, isLoading } = useQuery<ContactWithRelations[]>({
    queryKey: [`/api/marketing/contacts?type=${activeTab}`],
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });
  const regionsById = useMemo(() => {
    const m = new Map<string, Region>();
    regions.forEach((r) => m.set(r.id, r));
    return m;
  }, [regions]);

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients", "lookup"],
  });
  const clientsById = useMemo(() => {
    const m = new Map<string, Client>();
    clients.forEach((c) => m.set(c.id, c));
    return m;
  }, [clients]);

  const filtered = useMemo(() => {
    if (!contacts) return [];
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (q) {
        const hay = [c.firstName, c.lastName, c.email, c.outlet, c.city, c.beat]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (regionFilter.length > 0) {
        if (!c.regionIds.some((id) => regionFilter.includes(id))) return false;
      }
      return true;
    });
  }, [contacts, search, regionFilter]);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: emptyForm(activeTab),
  });

  // Reset form whenever the dialog opens for a new target.
  useEffect(() => {
    if (!dialogOpen) return;
    if (editing) {
      form.reset({
        type: (editing.type as ContactType) || activeTab,
        firstName: editing.firstName ?? "",
        lastName: editing.lastName ?? "",
        email: editing.email ?? "",
        phone: editing.phone ?? "",
        city: editing.city ?? "",
        state: editing.state ?? "",
        notes: editing.notes ?? "",
        outlet: editing.outlet ?? "",
        beat: editing.beat ?? "",
        mediaType: editing.mediaType ?? "",
        optInEmail: !!editing.optInEmail,
        optInSms: !!editing.optInSms,
        source: editing.source ?? "",
        regionIds: editing.regionIds ?? [],
        clientIds: editing.clientIds ?? [],
      });
    } else {
      form.reset(emptyForm(activeTab));
    }
  }, [dialogOpen, editing, activeTab, form]);

  const saveMutation = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      const payload = { ...values, type: activeTab };
      if (editing) {
        const res = await apiRequest("PATCH", `/api/marketing/contacts/${editing.id}`, payload);
        return res.json();
      }
      const res = await apiRequest("POST", "/api/marketing/contacts", payload);
      return res.json();
    },
    onSuccess: () => {
      // Invalidate both tab caches: a contact's type can change between
      // media↔audience on edit, so the other tab may also be stale.
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/contacts?type=media"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/contacts?type=audience"] });
      toast({ title: editing ? "Contact updated" : "Contact added" });
      setDialogOpen(false);
      setEditing(null);
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err?.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/marketing/contacts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/marketing/contacts?type=${activeTab}`] });
      toast({ title: "Contact removed" });
      setConfirmDelete(null);
    },
  });

  const suppressMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/marketing/contacts/${id}/suppress`, { reason: "manual" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/marketing/contacts?type=${activeTab}`] });
      toast({ title: "Contact suppressed" });
    },
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      const lines = importText.trim().split("\n").filter(Boolean);
      const [headerLine, ...rows] = lines;
      const headers = headerLine.split(",").map((h) => h.trim().toLowerCase());
      const records = rows.map((line) => {
        const values = line.split(",").map((v) => v.trim());
        const rec: any = { type: activeTab, source: "csv-import", optInEmail: activeTab === "audience" };
        headers.forEach((h, i) => {
          if (values[i]) rec[h] = values[i];
        });
        return rec;
      });
      const res = await apiRequest("POST", "/api/marketing/contacts/import", { contacts: records });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: [`/api/marketing/contacts?type=${activeTab}`] });
      toast({ title: `Imported ${data.imported} contacts` });
      setImportOpen(false);
      setImportText("");
    },
    onError: (err: any) => {
      toast({ title: "Import failed", description: err?.message, variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (c: ContactWithRelations) => {
    setEditing(c);
    setDialogOpen(true);
  };

  const onSubmit = (values: ContactFormValues) => saveMutation.mutate(values);

  return (
    <div className="h-full flex flex-col" data-testid="page-marketing-contacts">
      <div className="p-3 sm:p-6 pb-0 flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold" data-testid="text-page-title">
            Marketing Contacts
          </h1>
          <p className="text-sm text-muted-foreground hidden sm:block">
            Manage media outreach and audience subscribers. Click a row to edit.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, outlet, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-[260px]"
              data-testid="input-search"
            />
          </div>

          <RegionFilterPill
            regions={regions}
            value={regionFilter}
            onChange={setRegionFilter}
            open={regionFilterOpen}
            onOpenChange={setRegionFilterOpen}
          />

          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="button-import-contacts">
                <Upload className="w-4 h-4 mr-2" /> Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  Import {activeTab === "media" ? "Media" : "Audience"} Contacts
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Paste CSV with headers (e.g. firstName,lastName,email,phone,city,state
                {activeTab === "media" ? ",outlet,beat,mediaType" : ""}).
              </p>
              <Textarea
                rows={10}
                placeholder="firstName,lastName,email,..."
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                data-testid="input-import-csv"
              />
              <DialogFooter>
                <Button
                  onClick={() => importMutation.mutate()}
                  disabled={!importText.trim() || importMutation.isPending}
                  data-testid="button-confirm-import"
                >
                  {importMutation.isPending ? "Importing..." : "Import"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button size="sm" onClick={openCreate} data-testid="button-add-contact">
            <Plus className="w-4 h-4 mr-2" /> Add Contact
          </Button>
        </div>
      </div>

      <div className="px-3 sm:px-6 mt-3">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ContactType)}>
          <TabsList data-testid="tabs-contact-type">
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="audience" data-testid="tab-audience">Audience</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 overflow-auto mt-2 sm:mt-4 px-3 sm:px-6 pb-6">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="border rounded-md py-16 text-center text-sm text-muted-foreground"
            data-testid="text-empty-state"
          >
            No {activeTab} contacts match. Add one or import a CSV to get started.
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Name</th>
                  <th className="text-left font-medium px-3 py-2">Contact</th>
                  {activeTab === "media" ? (
                    <>
                      <th className="text-left font-medium px-3 py-2">Outlet · Beat</th>
                      <th className="text-left font-medium px-3 py-2">Type</th>
                    </>
                  ) : (
                    <th className="text-left font-medium px-3 py-2">Opt-in</th>
                  )}
                  <th className="text-left font-medium px-3 py-2">Regions</th>
                  <th className="text-left font-medium px-3 py-2">Past clients</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="px-3 py-2 w-[88px]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const name =
                    [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                    c.email ||
                    "Untitled contact";
                  return (
                    <tr
                      key={c.id}
                      className="border-t hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => openEdit(c)}
                      data-testid={`row-contact-${c.id}`}
                    >
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium" data-testid={`text-contact-name-${c.id}`}>
                          {name}
                        </div>
                        {c.city && (
                          <div className="text-xs text-muted-foreground">
                            {c.city}
                            {c.state ? `, ${c.state}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {c.email && <div className="truncate max-w-[220px]">{c.email}</div>}
                        {c.phone && (
                          <div className="text-xs text-muted-foreground">{c.phone}</div>
                        )}
                      </td>
                      {activeTab === "media" ? (
                        <>
                          <td className="px-3 py-2 align-top">
                            {c.outlet && <div>{c.outlet}</div>}
                            {c.beat && (
                              <div className="text-xs text-muted-foreground">{c.beat}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top text-xs">
                            {c.mediaType && <Badge variant="outline">{c.mediaType}</Badge>}
                          </td>
                        </>
                      ) : (
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-wrap gap-1">
                            {c.optInEmail && <Badge variant="outline">Email</Badge>}
                            {c.optInSms && <Badge variant="outline">SMS</Badge>}
                          </div>
                        </td>
                      )}
                      <td className="px-3 py-2 align-top">
                        <ChipList
                          items={c.regionIds
                            .map((id) => regionsById.get(id)?.name)
                            .filter((v): v is string => !!v)}
                          icon={<MapPin className="w-3 h-3" />}
                          empty="—"
                          testIdPrefix={`region-chip-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <ChipList
                          items={c.clientIds
                            .map((id) => clientsById.get(id)?.accountName)
                            .filter((v): v is string => !!v)}
                          icon={<Users className="w-3 h-3" />}
                          empty="—"
                          testIdPrefix={`client-chip-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        {c.suppressed && (
                          <Badge variant="destructive" data-testid={`badge-suppressed-${c.id}`}>
                            Suppressed
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {!c.suppressed && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => suppressMutation.mutate(c.id)}
                              data-testid={`button-suppress-${c.id}`}
                              title="Suppress"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmDelete(c)}
                            data-testid={`button-delete-${c.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit" : "New"} {activeTab === "media" ? "Media" : "Audience"} Contact
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
            >
              <div className="grid grid-cols-2 gap-3">
                <FormField control={form.control} name="firstName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl><Input {...field} data-testid="input-first-name" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="lastName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl><Input {...field} data-testid="input-last-name" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl><Input type="email" {...field} data-testid="input-email" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="phone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl><Input {...field} data-testid="input-phone" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="city" render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl><Input {...field} data-testid="input-city" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="state" render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <FormControl><Input {...field} data-testid="input-state" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {activeTab === "media" && (
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="outlet" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Outlet / Publication</FormLabel>
                      <FormControl><Input {...field} data-testid="input-outlet" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="beat" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Beat</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="comedy, music, family..." data-testid="input-beat" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="mediaType" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Media type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-media-type">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="print">Print</SelectItem>
                          <SelectItem value="podcast">Podcast</SelectItem>
                          <SelectItem value="radio">Radio</SelectItem>
                          <SelectItem value="tv">TV</SelectItem>
                          <SelectItem value="blog">Blog</SelectItem>
                          <SelectItem value="newsletter">Newsletter</SelectItem>
                          <SelectItem value="influencer">Influencer</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              )}

              {activeTab === "audience" && (
                <div className="flex gap-6">
                  <FormField control={form.control} name="optInEmail" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-opt-in-email"
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">Opted in to email</FormLabel>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="optInSms" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-opt-in-sms"
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">Opted in to SMS</FormLabel>
                    </FormItem>
                  )} />
                </div>
              )}

              {/* Multi-region (optional, both tabs) */}
              <FormField control={form.control} name="regionIds" render={({ field }) => (
                <FormItem>
                  <FormLabel>Regions (optional)</FormLabel>
                  <FormControl>
                    <MultiRegionPicker
                      regions={regions}
                      value={field.value || []}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              {/* Multi-client (both tabs) */}
              <FormField control={form.control} name="clientIds" render={({ field }) => (
                <FormItem>
                  <FormLabel>Past clients</FormLabel>
                  <FormControl>
                    <MultiClientPicker
                      clients={clients}
                      value={field.value || []}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="notes" render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} data-testid="input-notes" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saveMutation.isPending}
                  data-testid="button-save-contact"
                >
                  {saveMutation.isPending ? "Saving..." : editing ? "Save changes" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the contact and any region/client links. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
              data-testid="button-confirm-delete"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ChipList({
  items,
  icon,
  empty,
  testIdPrefix,
}: {
  items: string[];
  icon?: React.ReactNode;
  empty: string;
  testIdPrefix: string;
}) {
  if (items.length === 0) {
    return <span className="text-xs text-muted-foreground">{empty}</span>;
  }
  const visible = items.slice(0, 3);
  const overflow = items.length - visible.length;
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((label, i) => (
        <Badge
          key={`${label}-${i}`}
          variant="secondary"
          className="font-normal"
          data-testid={`${testIdPrefix}-${i}`}
        >
          {icon}
          <span className={icon ? "ml-1" : ""}>{label}</span>
        </Badge>
      ))}
      {overflow > 0 && (
        <Badge variant="outline" className="font-normal">
          +{overflow}
        </Badge>
      )}
    </div>
  );
}

function RegionFilterPill({
  regions,
  value,
  onChange,
  open,
  onOpenChange,
}: {
  regions: Region[];
  value: string[];
  onChange: (next: string[]) => void;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const label =
    value.length === 0
      ? "All regions"
      : value.length === 1
        ? regions.find((r) => r.id === value[0])?.name ?? "1 region"
        : `${value.length} regions`;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="justify-between font-normal"
          data-testid="button-region-filter"
        >
          <MapPin className="w-3.5 h-3.5 mr-1.5" />
          {label}
          {value.length > 0 ? (
            <X
              className="ml-2 h-3.5 w-3.5 opacity-60 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              data-testid="button-clear-region-filter"
            />
          ) : (
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Filter regions..." />
          <CommandList>
            <CommandEmpty>No regions.</CommandEmpty>
            <CommandGroup>
              {regions.map((r) => {
                const selected = value.includes(r.id);
                return (
                  <CommandItem
                    key={r.id}
                    value={r.name}
                    onSelect={() => {
                      onChange(
                        selected ? value.filter((id) => id !== r.id) : [...value, r.id],
                      );
                    }}
                    data-testid={`option-filter-region-${r.id}`}
                  >
                    <Check
                      className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")}
                    />
                    {r.name}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function MultiRegionPicker({
  regions,
  value,
  onChange,
}: {
  regions: Region[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = regions.filter((r) => value.includes(r.id));
  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal"
            data-testid="button-multi-region-picker"
          >
            <span className="truncate">
              {selected.length === 0
                ? "Add regions..."
                : `${selected.length} region${selected.length === 1 ? "" : "s"} selected`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search regions..." />
            <CommandList>
              <CommandEmpty>No regions found.</CommandEmpty>
              <CommandGroup>
                {regions.map((r) => {
                  const isSel = value.includes(r.id);
                  return (
                    <CommandItem
                      key={r.id}
                      value={r.name}
                      onSelect={() => {
                        onChange(
                          isSel ? value.filter((id) => id !== r.id) : [...value, r.id],
                        );
                      }}
                      data-testid={`option-region-${r.id}`}
                    >
                      <Check
                        className={cn("mr-2 h-4 w-4", isSel ? "opacity-100" : "opacity-0")}
                      />
                      {r.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((r) => (
            <Badge
              key={r.id}
              variant="secondary"
              className="font-normal pr-1"
              data-testid={`chip-selected-region-${r.id}`}
            >
              <MapPin className="w-3 h-3 mr-1" />
              {r.name}
              <button
                type="button"
                onClick={() => onChange(value.filter((id) => id !== r.id))}
                className="ml-1 opacity-60 hover:opacity-100"
                aria-label={`Remove ${r.name}`}
                data-testid={`button-remove-region-${r.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiClientPicker({
  clients,
  value,
  onChange,
}: {
  clients: Client[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = clients.filter((c) => value.includes(c.id));
  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal"
            data-testid="button-multi-client-picker"
          >
            <span className="truncate">
              {selected.length === 0
                ? "Link past clients..."
                : `${selected.length} client${selected.length === 1 ? "" : "s"} linked`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[360px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by account name..." />
            <CommandList>
              <CommandEmpty>No clients found.</CommandEmpty>
              <CommandGroup>
                {clients.slice(0, 300).map((c) => {
                  const isSel = value.includes(c.id);
                  return (
                    <CommandItem
                      key={c.id}
                      value={`${c.accountName ?? ""} ${c.city ?? ""} ${c.state ?? ""}`}
                      onSelect={() => {
                        onChange(
                          isSel ? value.filter((id) => id !== c.id) : [...value, c.id],
                        );
                      }}
                      data-testid={`option-client-${c.id}`}
                    >
                      <Check
                        className={cn("mr-2 h-4 w-4", isSel ? "opacity-100" : "opacity-0")}
                      />
                      <div className="flex flex-col">
                        <span className="text-sm">{c.accountName ?? "Unnamed"}</span>
                        <span className="text-xs text-muted-foreground">
                          {[c.clientType, c.city, c.state].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((c) => (
            <Badge
              key={c.id}
              variant="secondary"
              className="font-normal pr-1"
              data-testid={`chip-selected-client-${c.id}`}
            >
              <Users className="w-3 h-3 mr-1" />
              {c.accountName ?? "Unnamed"}
              <button
                type="button"
                onClick={() => onChange(value.filter((id) => id !== c.id))}
                className="ml-1 opacity-60 hover:opacity-100"
                aria-label={`Remove ${c.accountName ?? "client"}`}
                data-testid={`button-remove-client-${c.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
